import { BILLING_LIMITS, type BillingLimit } from '@aysar/contracts';
import { z, type ZodType } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';

import { type TierLimitRow } from '../tier-limit-map/tier-limit-map.util';

/** Translates a key, with interpolation. The caller's `t`. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** One box per lever, keyed by its ENCODED field name. Empty is `undefined`. */
export type TierLimitDraft = Record<string, number | undefined>;

/**
 * A lever key to the name its box carries on the form.
 *
 * **A dotted field name is a PATH**, not a key: the dynamic form reads and
 * writes values with `readPath`/`writePath` (that is how an object group's
 * property is addressed), so `nominations.dailyCount` would build
 * `{ nominations: { dailyCount: … } }` and the number would never reach the
 * lever it is named after — with nothing failing. The levers are the only
 * setting keys the portal edits as *form fields*, which is why this is the first
 * place it bites.
 */
export const LEVER_FIELD_NAMES: Readonly<Record<BillingLimit, string>> = Object.fromEntries(
  BILLING_LIMITS.map((lever) => [lever, lever.replaceAll('.', '__')]),
) as Record<BillingLimit, string>;

const LEVERS_BY_FIELD_NAME: ReadonlyMap<string, BillingLimit> = new Map(
  BILLING_LIMITS.map((lever) => [LEVER_FIELD_NAMES[lever], lever]),
);

/** The lever a box belongs to, or `null` for a name that is not one. */
export function leverOfFieldName(name: string): BillingLimit | null {
  return LEVERS_BY_FIELD_NAME.get(name) ?? null;
}

/**
 * The boxes, seeded from the tier's **own** overrides.
 *
 * Never from what the tier resolves to: seeding an inheriting box with the
 * inherited number would turn opening the dialog and pressing Save into an
 * override nobody asked for — and that tier would then silently stop following
 * the platform's own setting for ever (P23b1, one level up from a gender).
 */
export function draftOfRow(row: TierLimitRow): TierLimitDraft {
  return Object.fromEntries(BILLING_LIMITS.map((lever) => [LEVER_FIELD_NAMES[lever], row[lever]]));
}

/** The boxes back to a tier's overrides. An emptied box is no override at all. */
export function rowOfDraft(draft: TierLimitDraft): TierLimitRow {
  const row: Partial<Record<BillingLimit, number>> = {};

  for (const [name, value] of Object.entries(draft)) {
    const lever = leverOfFieldName(name);

    // `undefined` and not `null`: the kit's `number` control hands back
    // `undefined` for an emptied box, deliberately, so that it is never
    // confused with a deliberate `0`.
    if (lever !== null && value !== undefined) {
      row[lever] = value;
    }
  }

  return row;
}

/**
 * The editor's schema — one optional whole number per lever.
 *
 * Mirrors the api's own `LEVERS` object (`z.number().int().min(0).optional()`),
 * so a refusal is found here rather than as a 422 on a control the operator has
 * already stopped looking at.
 */
export function tierLimitSchema(): ZodType<TierLimitDraft, TierLimitDraft> {
  return z
    .object(
      Object.fromEntries(
        BILLING_LIMITS.map((lever) => [
          LEVER_FIELD_NAMES[lever],
          z.number().int().min(0).optional(),
        ]),
      ),
    )
    .strict() as ZodType<TierLimitDraft, TierLimitDraft>;
}

export interface TierLimitFieldsInput {
  t: Translate;
  /** What the lever resolves to today, in words, for the box's own sentence. */
  describe: (lever: BillingLimit) => string;
  isDisabled: boolean;
}

/**
 * One number box per lever, each saying what an empty box is worth.
 *
 * The sentence is the point: an inheriting box is **empty**, and the number it
 * inherits is per gender while this override is not — so without it an operator
 * is typing over a value they cannot see (W8's "every setting shows its
 * effective resolved value").
 */
export function tierLimitFields({ t, describe, isDisabled }: TierLimitFieldsInput): FieldDef[] {
  return BILLING_LIMITS.map((lever) => ({
    name: LEVER_FIELD_NAMES[lever],
    kind: 'number' as const,
    // A dotted i18n key resolves: i18next's `deepFind` re-joins the split
    // tokens onto the flat `"nominations.dailyCount"` node (P26a, probed).
    label: t(`tierLimits.levers.${lever}.label`),
    description: `${t(`tierLimits.levers.${lever}.description`)} — ${describe(lever)}`,
    min: 0,
    step: 1,
    // `isDisabled` alone: `NumberFieldDef` carries no `isReadOnly`, because the
    // control behind it is a plain number input that cannot honour one — and a
    // def member the control ignores is a def that lies (P25d2). Read-only in
    // this kit IS the disabled field (P22b1).
    isDisabled,
  }));
}
